import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { clientConfig } from '../../client'
import { useOverlay, usePage, usePageViewInDay } from '../../contexts'
import { IPageMeta, IVideo } from '../../types'
import { Player } from '../GridBlocks/Reels/Player/Player'
import { PlayerProvider } from '../GridBlocks/Reels/PlayerContext'
import { IPlayerStyle } from '../GridBlocks/Reels/type'
import { useModalDialog } from '../GridBlocks/Reels/focusTrap'
import { dfpUtils } from '../Player/Helpers'
import { reelsHe } from '../../locales/reels.he'

type Props = {
  video: IVideo
  pageMeta: IPageMeta
  title: string
  poster: string
}

/**
 * Figma node 23009:300659 (514x914): 30px between the title and the progress
 * row, 18px timecodes, 12px side padding, 30px from the bottom, 30px close and
 * sound icons, and a play button in the middle of the player.
 *
 * The title is the one value not taken from there: the design draws it at 30px
 * on a 514px wide frame, and the player runs narrower than that, so it was
 * asked for at 18px instead.
 */
const ITEM_PLAYER_STYLE: IPlayerStyle = {
  borderCorner: 'square',
  seekBar: 'linear',
  overlayPadding: 12,
  overlayPaddingMobile: 12,
  seekBarTimeSize: 18,
  overlayInfoPaddingBottom: 30,
  overlayInfoPaddingBottomMobile: 30,
  overlayInfoGap: 30,
  titleFontSize: 18,
  showCenterPlayButton: true,
  controlIconSize: 30,
}

/**
 * RESH13-1138
 * Fullscreen player for a vertical video on an item page. It reuses the
 * vertical player of the reels feed, but without the feed: there is no
 * swiping to other videos, the analytics keep the event names of the regular
 * item-page player, and the pre-roll comes from the Vertical_*Item ad units
 * through IMA (reels play their ads as separate slides instead).
 */
export const ItemVerticalPlayer = ({
  video,
  pageMeta,
  title,
  poster,
}: Props) => {
  const { handleClose } = useOverlay()
  const { pageViewInDayCount } = usePageViewInDay()
  const page = usePage()

  /**
   * The page behind the player must not scroll. Locking the body keeps the
   * scroll position, so closing the player returns to the same spot.
   */
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const adTagUrl = useMemo(
    () =>
      dfpUtils.getAdVerticalItem({
        pagetypeMeta: pageMeta.pageType,
        referenceId: video.name,
        levels: pageMeta.levels,
        pageViewedInDay: pageViewInDayCount,
        item: page?.Content?.Item,
        environment: clientConfig.adTargetEnv || 'prod',
        katuraId: video.kalturaId,
      }),
    // built once per opening on purpose - a new correlator per ad request
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // keyboard: focus in, Escape closes, Tab stays inside, focus back to the card
  const wrapRef = useRef<HTMLDivElement>(null)
  const onDialogKeyDown = useModalDialog(wrapRef, handleClose)

  // the player waits for the Kaltura script itself, and the overlay (with its
  // close button) shows meanwhile over the poster
  return (
    <PlayerProvider>
      <PlayerWrap
        ref={wrapRef}
        role='dialog'
        aria-modal='true'
        aria-label={title || reelsHe.dialog}
        tabIndex={-1}
        onKeyDown={onDialogKeyDown}
        onClick={event => event.stopPropagation()}
      >
        <Player
          slidedData={{
            video,
            realIndex: 0,
            img: poster,
            title,
          }}
          index={0}
          isActive={true}
          isVisible={true}
          isMiniSeries={false}
          handleClosePlayer={handleClose}
          eventPrefix=''
          appEventPrefix={false}
          adTagUrl={adTagUrl}
          loop={false}
          playerIdOverride='vertical-player-item'
          playerStyle={ITEM_PLAYER_STYLE}
        />
      </PlayerWrap>
    </PlayerProvider>
  )
}

const PlayerWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  /* focus is moved here when the player opens */
  &:focus {
    outline: none;
  }
`
